import React, { useState } from "react";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import { FileText, Clock, Upload, Zap } from "lucide-react";
import { QuoteRequest } from "../../types/rate-analysis";
import { adminApi } from "../../utils/adminApi";
import Toast from "../Toast";

const AdminDashboard: React.FC = () => {
  const [quoteId, setQuoteId] = useState("");
  const [isRunningPricing, setIsRunningPricing] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState<"success" | "error">("success");

  // Fetch quote requests without analysis
  const { data: pendingRequests, isLoading: isLoadingPending } = useQuery<
    QuoteRequest[]
  >("pendingQuoteRequests", async () => {
    return adminApi.get("/quote-requests/pending");
  });

  // Fetch quote requests with analysis
  const { data: analyzedRequests, isLoading: isLoadingAnalyzed } = useQuery<
    QuoteRequest[]
  >("analyzedQuoteRequests", async () => {
    return adminApi.get("/quote-requests/analyzed");
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleRunAutoPricing = async () => {
    if (!quoteId.trim()) {
      setToastMessage("Please enter a quote ID");
      setToastType("error");
      setShowToast(true);
      return;
    }

    setIsRunningPricing(true);
    try {
      const result = await adminApi.post(
        `/quote-requests/${quoteId.trim()}/run-auto-pricing`,
        {}
      );

      if (result.success) {
        setToastMessage(
          `Auto-pricing complete! Created ${result.quotesCreated} quotes${result.recommendedRate ? ` (recommended: ${result.recommendedRate}%)` : ""}`
        );
        setToastType("success");
      } else {
        setToastMessage(result.reason || "Auto-pricing failed");
        setToastType("error");
      }
    } catch (error: any) {
      setToastMessage(error.message || "Failed to run auto-pricing");
      setToastType("error");
    } finally {
      setIsRunningPricing(false);
      setShowToast(true);
      setQuoteId("");
    }
  };

  const renderQuoteRequestTable = (
    requests: QuoteRequest[] | undefined,
    isLoading: boolean,
    type: "pending" | "analyzed"
  ) => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      );
    }

    if (!requests?.length) {
      return (
        <div className="text-center py-8 text-gray-500">
          No {type} quote requests found
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                Date
              </th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                Property Value
              </th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                Down Payment
              </th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                Credit Score
              </th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                Zip Code
              </th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {requests.map((request) => (
              <tr
                key={request.id}
                className="border-b border-gray-100 hover:bg-gray-50"
              >
                <td className="py-3 px-4 text-gray-700">
                  {formatDate(request.created_at)}
                </td>
                <td className="py-3 px-4 text-gray-700">
                  ${request.property_value?.toLocaleString()}
                </td>
                <td className="py-3 px-4 text-gray-700">
                  ${request.down_payment?.toLocaleString()}
                </td>
                <td className="py-3 px-4 text-gray-700">
                  {request.credit_score}
                </td>
                <td className="py-3 px-4 text-gray-700">{request.zip_code}</td>
                <td className="py-3 px-4">
                  <Link
                    to={`/admin/analysis/${request.id}`}
                    className="inline-flex items-center text-primary hover:text-primary-dark transition-colors"
                  >
                    {type === "pending" ? (
                      <>
                        <Clock size={16} className="mr-1" />
                        Analyze
                      </>
                    ) : (
                      <>
                        <FileText size={16} className="mr-1" />
                        View
                      </>
                    )}
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Link
          to="/admin/rate-sheets"
          className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-primary/10 rounded-lg">
              <Upload className="text-primary" size={24} />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-gray-900">
                Rate Sheet Management
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Upload and manage rate sheets
              </p>
            </div>
          </div>
        </Link>

        {/* Run Auto Pricing */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center space-x-4 mb-4">
            <div className="p-3 bg-amber-100 rounded-lg">
              <Zap className="text-amber-600" size={24} />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-gray-900">
                Run Auto Pricing
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Generate pricing for a quote
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={quoteId}
              onChange={(e) => setQuoteId(e.target.value)}
              placeholder="Enter Quote ID"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleRunAutoPricing();
              }}
            />
            <button
              onClick={handleRunAutoPricing}
              disabled={isRunningPricing}
              className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isRunningPricing ? "Running..." : "Run"}
            </button>
          </div>
        </div>
      </div>

      {/* Pending Analysis Section */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-heading font-semibold text-gray-900">
            Pending Analysis
          </h2>
        </div>
        <div className="p-6">
          {renderQuoteRequestTable(
            pendingRequests,
            isLoadingPending,
            "pending"
          )}
        </div>
      </div>

      {/* Analyzed Requests Section */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-heading font-semibold text-gray-900">
            Analyzed Requests
          </h2>
        </div>
        <div className="p-6">
          {renderQuoteRequestTable(
            analyzedRequests,
            isLoadingAnalyzed,
            "analyzed"
          )}
        </div>
      </div>

      <Toast
        message={toastMessage}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
        type={toastType}
      />
    </div>
  );
};

export default AdminDashboard;
